import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Radio, RadioGroup, Select, Stack, TextField, Typography } from '@mui/material'
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { IArchivedAndReportPeriodDetails } from '../../../shared/services/report-period/report-period.model'
import moment from 'moment'
import TargetSettingService from '../../../shared/services/target-setting/target-setting.service'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { useFlagEmissionsCapability } from '../../../hooks/use-flag-emissions-capability'
import { targetYearOptions } from './target-year-options'

export const ReductionForm: FunctionComponent<{ baseYear: IArchivedAndReportPeriodDetails, onPlanCreated: () => void }> = ({ baseYear, onPlanCreated }) => {
  //Services
  const targetSettingService = new TargetSettingService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  const { flagEnabled } = useFlagEmissionsCapability()
  //State
  const [showReductionPercentages, setShowReductionPercentages] = useState(false)
  //Loading State
  const [isSubmitting, setIsSubmitting] = useState(false)

  const targetYearSchema = Yup.object().shape({
    baseYear: Yup.string(),
    calculationMethod: Yup.string(),
    targetYear: Yup.string().required('Target year is required'),
    scopeOneReductionPercentage: Yup.number().min(0).when('calculationMethod', (calculationMethod, schema) => {
      if (typeof calculationMethod === 'string' && calculationMethod === 'custom') {
        const hasScopeOneData = (baseYear.totalScopeOneTCEUk + baseYear.totalScopeOneTCEGlobal) > 0
        return hasScopeOneData ? schema.required('Scope One reduction percentage is required') : schema.nullable()
      }
      return schema.nullable()
    }),
    scopeTwoLocationReductionPercentage: Yup.number().min(0).when('calculationMethod', (calculationMethod, schema) => {
      if (typeof calculationMethod === 'string' && calculationMethod === 'custom') {
        const hasScopeTwoData = (baseYear.totalScopeTwoLocationTCEUk + baseYear.totalScopeTwoLocationTCEGlobal) > 0
        return hasScopeTwoData
          ? schema.required('Scope Two Location-based reduction percentage is required')
          : schema.nullable()
      }
      return schema.nullable()
    }),
    scopeTwoMarketReductionPercentage: Yup.number().min(0).when('calculationMethod', (calculationMethod, schema) => {
      if (typeof calculationMethod === 'string' && calculationMethod === 'custom') {
        const hasScopeTwoData = (baseYear.totalScopeTwoMarketTCEUk + baseYear.totalScopeTwoMarketTCEGlobal) > 0
        return hasScopeTwoData
          ? schema.required('Scope Two Market-based reduction percentage is required')
          : schema.nullable()
      }
      return schema.nullable()
    }),
    scopeThreeReductionPercentage: Yup.number().min(0).when('calculationMethod', (calculationMethod, schema) => {
      if (typeof calculationMethod === 'string' && calculationMethod === 'custom') {
        const hasScopeThreeData = (baseYear.totalScopeThreeTCEUk + baseYear.totalScopeThreeTCEGlobal) > 0
        return hasScopeThreeData
          ? schema.required('Scope Three reduction percentage is required')
          : schema.nullable()
      }
      return schema.nullable()
    }),
    flagReductionPercentage: Yup.number().min(0).nullable(),
    annualGrowthPercentage: Yup.number().nullable(),
  })

  const formik = useFormik({
    initialValues: {
      baseYear: moment(baseYear.calendarYear).year().toString(),
      targetYear: '',
      calculationMethod: '1.5c',
      scopeOneReductionPercentage: undefined,
      scopeTwoMarketReductionPercentage: undefined,
      scopeTwoLocationReductionPercentage: undefined,
      scopeThreeReductionPercentage: undefined,
      includeFlagTargets: false,
      flagReductionPercentage: undefined as number | undefined,
      annualGrowthPercentage: undefined as number | undefined,
    },
    validationSchema: targetYearSchema,
    onSubmit: (values) => {
      if (isSubmitting) return
      setIsSubmitting(true)
      targetSettingService
        .saveTarget({
          ...values,
          includeFlagTargets: flagEnabled ? values.includeFlagTargets : false,
        })
        .then(() => {
          onPlanCreated()
          setIsSubmitting(false)
        })
        .catch((err) => {
          userNotificationsContext.openSnackbar({
            open: true,
            message: 'There was an error, please try again',
            backgroundColor: 'text.primary',
          })
          setIsSubmitting(false)
          console.log(err)
        })
    },
  })

  useEffect(() => {
    if (!showReductionPercentages) {
      formik.setFieldValue('scopeOneReductionPercentage', null)
      formik.setFieldValue('scopeTwoMarketReductionPercentage', null)
      formik.setFieldValue('scopeTwoLocationReductionPercentage', null)
      formik.setFieldValue('scopeThreeReductionPercentage', null)
    }
  }, [showReductionPercentages])

  const calculationMethodHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('calculationMethod', e.target.value)
    if (e.target.value === '1.5c') setShowReductionPercentages(false)
    if (e.target.value === 'custom') setShowReductionPercentages(true)
  }

  const cancelHandler = () => {
    formik.resetForm()
    setShowReductionPercentages(false)
  }

  const submitHandler = () => {
    userNotificationsContext
      .openDialog({
        active: true,
        title: 'Confirmation',
        message: `By generating your reduction plan, you will no longer able to make changes to your base year.`,
        buttonCopy: 'Continue',
        cancelButtonCopy: 'Cancel',
      })
      .then((response) => {
        if (response) formik.submitForm()
      })
  }

  return (
    <Box sx={{ borderColor: 'lightGrey.main', borderStyle: 'solid', borderWidth: '1px', borderRadius: '10px', p: 4 }}>
      <Box component="form">
        <Stack display="flex" flexDirection="column">
          <Typography variant="h6" sx={{ mb: 2 }}>Objectives</Typography>
          <FormControl sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>Reduction Target Calculation Method</Typography>
            <RadioGroup
              onChange={calculationMethodHandler}
              id="calculationMethod"
              name="calculationMethod"
              value={formik.values.calculationMethod}
              sx={{ width: '100%', display: 'flex', flexDirection: 'column', color: 'text.secondary' }}
            >
              <FormControlLabel value="1.5c" control={<Radio />} label="1.5C" sx={{ mb: -1.5 }} />
              <FormControlLabel value="custom" control={<Radio />} label="Custom" />
            </RadioGroup>
          </FormControl>
          <FormControl disabled sx={{ mb: 2 }}>
            <InputLabel shrink>Base Year</InputLabel>
            <TextField label="Base Year" sx={{ color: 'text.secondary' }} value={formik.values.baseYear} disabled />
          </FormControl>
          {showReductionPercentages && (
            <FormControl disabled sx={{ mb: 2 }}>
              <InputLabel shrink>Scope 1 (tCO2e)</InputLabel>
              <TextField label="Scope 1 (tCO2e)" sx={{ color: 'text.secondary' }} value={(Number(baseYear.totalScopeOneTCEGlobal ?? 0) + Number(baseYear.totalScopeOneTCEUk ?? 0)).toFixed(2)} disabled />
            </FormControl>
          )}
          {showReductionPercentages && baseYear.totalScopeOneTCEUk + baseYear.totalScopeOneTCEGlobal !== 0 && (
            <FormControl sx={{ mb: 2 }}>
              <InputLabel htmlFor="scopeOneReductionPercentage">Scope 1 Reduction Objective (%)</InputLabel>
              <OutlinedInput
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position="end"><Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
                label="scopeOneReductionPercentage"
                sx={{ color: 'text.secondary' }}
                value={formik.values.scopeOneReductionPercentage}
                type="number"
                id="scopeOneReductionPercentage"
                name="scopeOneReductionPercentage"
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.scopeOneReductionPercentage && Boolean(formik.errors.scopeOneReductionPercentage)}
              />
              {formik.touched.scopeOneReductionPercentage && formik.errors.scopeOneReductionPercentage && (
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.scopeOneReductionPercentage}</FormHelperText>
              )}
            </FormControl>
          )}
          {showReductionPercentages && (
            <FormControl disabled sx={{ mb: 2 }}>
              <InputLabel shrink>Scope 2 Market-Based (tCO2e)</InputLabel>
              <TextField label="Scope 2 Market-Based (tCO2e)" sx={{ color: 'text.secondary' }} value={(Number(baseYear.totalScopeTwoMarketTCEUk ?? 0) + Number(baseYear.totalScopeTwoMarketTCEGlobal ?? 0)).toFixed(2)} disabled />
            </FormControl>
          )}
          {showReductionPercentages && baseYear.totalScopeTwoMarketTCEUk + baseYear.totalScopeTwoMarketTCEGlobal !== 0 && (
            <FormControl sx={{ mb: 2 }}>
              <InputLabel htmlFor="scopeTwoMarketReductionPercentage">Scope 2 Market-Based Reduction Objective (%)</InputLabel>
              <OutlinedInput
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position="end"><Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
                label="scopeTwoMarketReductionPercentage"
                sx={{ color: 'text.secondary' }}
                value={formik.values.scopeTwoMarketReductionPercentage}
                type="number"
                id="scopeTwoMarketReductionPercentage"
                name="scopeTwoMarketReductionPercentage"
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.scopeTwoMarketReductionPercentage && Boolean(formik.errors.scopeTwoMarketReductionPercentage)}
              />
              {formik.touched.scopeTwoMarketReductionPercentage && formik.errors.scopeTwoMarketReductionPercentage && (
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.scopeTwoMarketReductionPercentage}</FormHelperText>
              )}
            </FormControl>
          )}
          {showReductionPercentages && (
            <FormControl disabled sx={{ mb: 2 }}>
              <InputLabel shrink>Scope 2 Location-Based (tCO2e)</InputLabel>
              <TextField label="Scope 2 Location-Based (tCO2e)" sx={{ color: 'text.secondary' }} value={(Number(baseYear.totalScopeTwoLocationTCEUk ?? 0) + Number(baseYear.totalScopeTwoLocationTCEGlobal ?? 0)).toFixed(2)} disabled />
            </FormControl>
          )}
          {showReductionPercentages && baseYear.totalScopeTwoLocationTCEUk + baseYear.totalScopeTwoLocationTCEGlobal !== 0 && (
            <FormControl sx={{ mb: 2 }}>
              <InputLabel htmlFor="scopeTwoLocationReductionPercentage">Scope 2 Location-Based Reduction Objective (%)</InputLabel>
              <OutlinedInput
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position="end"><Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
                label="scopeTwoLocationReductionPercentage"
                sx={{ color: 'text.secondary' }}
                value={formik.values.scopeTwoLocationReductionPercentage}
                type="number"
                id="scopeTwoLocationReductionPercentage"
                name="scopeTwoLocationReductionPercentage"
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.scopeTwoLocationReductionPercentage && Boolean(formik.errors.scopeTwoLocationReductionPercentage)}
              />
              {formik.touched.scopeTwoLocationReductionPercentage && formik.errors.scopeTwoLocationReductionPercentage && (
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.scopeTwoLocationReductionPercentage}</FormHelperText>
              )}
            </FormControl>
          )}
          {showReductionPercentages && (
            <FormControl disabled sx={{ mb: 2 }}>
              <InputLabel shrink>Scope 3 (tCO2e)</InputLabel>
              <TextField label="Scope 3 (tCO2e)" sx={{ color: 'text.secondary' }} value={(Number(baseYear.totalScopeThreeTCEUk ?? 0) + Number(baseYear.totalScopeThreeTCEGlobal ?? 0)).toFixed(2)} disabled />
            </FormControl>
          )}
          {showReductionPercentages && baseYear.totalScopeThreeTCEUk + baseYear.totalScopeThreeTCEGlobal !== 0 && (
            <FormControl sx={{ mb: 2 }}>
              <InputLabel htmlFor="scopeThreeReductionPercentage">Scope 3 Reduction Objective (%)</InputLabel>
              <OutlinedInput
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position="end"><Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
                label="scopeThreeReductionPercentage"
                sx={{ color: 'text.secondary' }}
                value={formik.values.scopeThreeReductionPercentage}
                type="number"
                id="scopeThreeReductionPercentage"
                name="scopeThreeReductionPercentage"
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.scopeThreeReductionPercentage && Boolean(formik.errors.scopeThreeReductionPercentage)}
              />
              {formik.touched.scopeThreeReductionPercentage && formik.errors.scopeThreeReductionPercentage && (
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.scopeThreeReductionPercentage}</FormHelperText>
              )}
            </FormControl>
          )}
          {flagEnabled && (
            <FormControl sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formik.values.includeFlagTargets}
                    onChange={(e) => {
                      formik.setFieldValue('includeFlagTargets', e.target.checked)
                      if (!e.target.checked) formik.setFieldValue('flagReductionPercentage', undefined)
                    }}
                    name="includeFlagTargets"
                  />
                }
                label="Include FLAG stream reduction targets"
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', pl: 1, mt: 0.5 }}>
                Uses net FLAG emissions from the base year. For custom plans, the FLAG reduction % defaults to your Scope 3 objective unless you set an override below.
              </Typography>
            </FormControl>
          )}
          {flagEnabled && formik.values.includeFlagTargets && showReductionPercentages && (
            <FormControl sx={{ mb: 2 }}>
              <InputLabel htmlFor="flagReductionPercentage">FLAG reduction objective (% override)</InputLabel>
              <OutlinedInput
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position="end"><Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
                label="flagReductionPercentage"
                sx={{ color: 'text.secondary' }}
                value={formik.values.flagReductionPercentage ?? ''}
                type="number"
                id="flagReductionPercentage"
                name="flagReductionPercentage"
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.flagReductionPercentage && Boolean(formik.errors.flagReductionPercentage)}
              />
              {formik.touched.flagReductionPercentage && formik.errors.flagReductionPercentage && (
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.flagReductionPercentage}</FormHelperText>
              )}
            </FormControl>
          )}
          <FormControl sx={{ mb: 2 }}>
            <InputLabel htmlFor="annualGrowthPercentage">Expected annual business growth (optional)</InputLabel>
            <OutlinedInput
              onChange={formik.handleChange}
              endAdornment={<InputAdornment position="end"><Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
              label="Expected annual business growth (optional)"
              sx={{ color: 'text.secondary' }}
              value={formik.values.annualGrowthPercentage ?? ''}
              type="number"
              id="annualGrowthPercentage"
              name="annualGrowthPercentage"
              inputProps={{ step: 0.1 }}
              error={formik.touched.annualGrowthPercentage && Boolean(formik.errors.annualGrowthPercentage)}
            />
            <FormHelperText sx={{ marginLeft: '16px !important' }}>
              Applied to projections so business-as-usual reflects expected growth. Leave blank for a flat baseline.
            </FormHelperText>
            {formik.touched.annualGrowthPercentage && formik.errors.annualGrowthPercentage && (
              <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.annualGrowthPercentage}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="targetYear">Target Year</InputLabel>
            <Select
              id="targetYear"
              label="targetYear"
              name="targetYear"
              value={formik.values.targetYear}
              onChange={formik.handleChange}
              error={formik.touched.targetYear && Boolean(formik.errors.targetYear)}
              sx={{ color: 'text.primary' }}
            >
              {targetYearOptions(moment(baseYear.endDate).year()).map((year, index) => (
                <MenuItem key={index} value={year}>{year}</MenuItem>
              ))}
            </Select>
            {formik.touched.targetYear && formik.errors.targetYear && (
              <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>{formik.errors.targetYear}</FormHelperText>
            )}
          </FormControl>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={cancelHandler}
              submitHandler={submitHandler}
              buttonInitialText="Generate Plan"
              buttonLoadingText="Generating Plan"
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  )
}
